import { use } from '../../core/use'
import { sync } from '../../core/lifecycle'
import { hook } from '../../core/hook'
import { SuspenseCtx } from './suspenseContext'
import type { SuspenseProps, SuspenseContextValue } from './types'
import type { FNodeChild } from '../types'

/**
 * Suspense component that shows fallback while children are loading
 *
 * @example
 * ```tsx
 * const Dashboard = lazy(() => import('./Dashboard'))
 *
 * function App() {
 *   return (
 *     <Suspense fallback={<div>Loading...</div>}>
 *       <Dashboard />
 *     </Suspense>
 *   )
 * }
 * ```
 */
export function Suspense(props: SuspenseProps): FNodeChild {
  const { fallback, children } = props

  // Track pending promises using hook for mutable Set
  const pendingSet = hook(() => new Set<Promise<unknown>>())
  const [, setPendingCount] = use(0)
  const [showFallback, setShowFallback] = use(false)

  // Register function for lazy components
  const register = (promise: Promise<unknown>) => {
    if (!pendingSet.has(promise)) {
      // Add to pending set
      pendingSet.add(promise)
      sync(() => {
        setPendingCount(c => c + 1)
        setShowFallback(true)
      })

      // Wait for resolution
      promise.finally(() => {
        pendingSet.delete(promise)
        const newCount = pendingSet.size
        sync(() => {
          setPendingCount(() => newCount)
          if (newCount === 0) {
            setShowFallback(false)
          }
        })
      })
    }
  }

  const contextValue: SuspenseContextValue = {
    register,
    hasBoundary: true
  }

  // Render fallback or children based on pending state
  const content = showFallback ? fallback : children

  // Wrap content with Suspense context provider
  return {
    type: SuspenseCtx.Provider as unknown as (props: Record<string, unknown>) => unknown,
    props: { value: contextValue as unknown },
    children: [content],
    key: undefined
  }
}
